// Iter-207h (Phase 3) — NDF Drive Health Check page.
// Same UX pattern as CabinetExpensesDriveHealth / SuppliersDriveHealth.
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, RefreshCw, HardDrive, FileText,
  Settings as SettingsIcon, Wrench, User as UserIcon,
} from "lucide-react";
import VatBackfillPanel from "../components/VatBackfillPanel";

const STATUS_META = {
  ok: { color: "emerald", label: "OK", emoji: "✅" },
  out_of_sync: { color: "amber", label: "Désynchronisé", emoji: "⚠️" },
  missing_drive_upload: { color: "red", label: "À uploader", emoji: "📥" },
  not_applicable: { color: "zinc", label: "N/A (Brouillon)", emoji: "—" },
};

export default function NdfDriveHealth() {
  const { user } = useAuth();
  const isAdmin = user?.role === "CEO" || user?.role === "ADMIN";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remediating, setRemediating] = useState(false);
  const [showParentModal, setShowParentModal] = useState(false);
  const [parentInput, setParentInput] = useState("");
  const [savingParent, setSavingParent] = useState(false);
  const [parentInfo, setParentInfo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/expenses/ndf-drive-health-check");
      setData(r.data);
      setShowParentModal(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "drive_parent_missing") {
        setShowParentModal(true);
        try {
          const p = await api.get("/admin/ndf-drive-parent");
          setParentInfo(p.data);
        } catch (_) { /* noop */ }
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Échec du Health Check"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveParent = async () => {
    if (!parentInput.trim()) {
      toast.error("Veuillez coller l'URL ou l'ID du dossier Drive parent.");
      return;
    }
    setSavingParent(true);
    try {
      await api.post("/admin/ndf-drive-parent", { parent_id_or_url: parentInput.trim() });
      toast.success("Dossier parent enregistré.");
      setParentInput("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSavingParent(false);
    }
  };

  const remediate = async () => {
    if (!data?.remediable_count) {
      toast.info("Aucune NDF à synchroniser.");
      return;
    }
    if (!window.confirm(`Synchroniser ${data.remediable_count} NDF vers Drive ?`)) return;
    setRemediating(true);
    try {
      const r = await api.post("/expenses/ndf-drive-remediate");
      const { uploaded, moved, failed } = r.data;
      toast.success(`${uploaded} uploadée(s) · ${moved} déplacée(s) · ${failed} échec(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Échec de la synchronisation");
    } finally {
      setRemediating(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="ndf-drive-health-page">
      <header className="space-y-1">
        <h1 className="text-2xl font-light tracking-tight flex items-center gap-2">
          <HardDrive size={22} /> Notes de frais — Drive Health Check
        </h1>
        <p className="text-sm text-zinc-600 max-w-3xl leading-relaxed">
          Audite les notes de frais et vérifie qu'elles sont bien archivées sur Google Drive dans la structure <code>{`{parent}/{Nom Collaborateur}/{Soumise|Validée|Réglée|Rejetée}/`}</code>. Les transitions de statut déplacent automatiquement le PDF.
          {" "}Les <strong>justificatifs de règlement</strong> (preuves de paiement jointes lors du « Marquer payée ») sont archivés dans <code data-testid="ndf-drive-proof-path">{`{parent}/{Nom Collaborateur}/Réglée/`}</code>.
        </p>
      </header>

      {/* Iter-207bb — Retroactive VAT backfill (CEO/ADMIN only) */}
      {isAdmin && <VatBackfillPanel />}

      {data?.parent_id && (
        <div className="text-xs text-zinc-500 font-mono flex items-center gap-2">
          <SettingsIcon size={12} /> Dossier parent : <code className="text-zinc-700">{data.parent_id}</code>
          <button onClick={() => setShowParentModal(true)} data-testid="ndf-drive-change-parent" className="text-blue-600 hover:underline">
            modifier
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={load} disabled={loading}
          data-testid="ndf-drive-refresh"
          className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 text-xs font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Relancer
        </button>
        {data && data.remediable_count > 0 && (
          <button
            onClick={remediate} disabled={remediating}
            data-testid="ndf-drive-remediate-all"
            className="inline-flex items-center gap-1.5 px-3 h-8 border border-blue-200 bg-blue-50 text-blue-700 text-xs font-mono uppercase tracking-wider rounded-md hover:bg-blue-100 disabled:opacity-50"
          >
            {remediating ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />} Synchroniser ({data.remediable_count})
          </button>
        )}
        {data && (
          <div className="ml-auto text-xs text-zinc-500 font-mono" data-testid="ndf-drive-scanned">
            {data.scanned} NDF scannée(s)
          </div>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(data.counters || {}).map(([k, v]) => {
            const meta = STATUS_META[k] || { color: "zinc", label: k };
            return (
              <div key={k} className="p-3 rounded-md border border-zinc-100 bg-white" data-testid={`ndf-drive-counter-${k}`}>
                <div className={`text-[10px] font-mono uppercase tracking-wider text-${meta.color}-700`}>{meta.emoji} {meta.label}</div>
                <div className="text-xl font-light text-zinc-800 mt-1">{v}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Per-user breakdown */}
      <div className="border border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600 font-mono uppercase text-[10px] tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Collaborateur</th>
              <th className="px-3 py-2 text-right">OK</th>
              <th className="px-3 py-2 text-right">Désynchronisé</th>
              <th className="px-3 py-2 text-right">À uploader</th>
              <th className="px-3 py-2 text-right">Brouillons</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {(!data?.per_user || data.per_user.length === 0) && (
              <tr><td colSpan={5} className="px-3 py-12 text-center text-zinc-500">
                {loading ? "Chargement…" : "Aucune NDF enregistrée."}
              </td></tr>
            )}
            {data?.per_user?.map((u) => (
              <tr key={u.user_name} data-testid={`ndf-drive-user-${u.user_name}`}>
                <td className="px-3 py-2 font-medium text-zinc-800 flex items-center gap-1.5">
                  <UserIcon size={11} className="text-zinc-400" /> {u.user_name}
                </td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{u.ok || 0}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-700">{u.out_of_sync || 0}</td>
                <td className="px-3 py-2 text-right font-mono text-red-700">{u.missing_drive_upload || 0}</td>
                <td className="px-3 py-2 text-right font-mono text-zinc-500">{u.not_applicable || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Itemized list of non-OK NDFs */}
      {data?.items?.length > 0 && (
        <div className="border border-zinc-200 rounded-md overflow-hidden">
          <div className="bg-zinc-50 px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-600">
            Détail des NDF non conformes ({data.items.length})
          </div>
          <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full text-xs">
            <thead className="bg-white text-zinc-500 font-mono uppercase text-[10px] tracking-wider border-b border-zinc-100">
              <tr>
                <th className="px-3 py-2 text-left">Collab.</th>
                <th className="px-3 py-2 text-left">Période</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-left">Dossier actuel</th>
                <th className="px-3 py-2 text-left">Dossier attendu</th>
                <th className="px-3 py-2 text-left">Anomalie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.items.slice(0, 100).map((it) => {
                const meta = STATUS_META[it.classification] || { color: "zinc", label: it.classification };
                return (
                  <tr key={it.expense_note_id} data-testid={`ndf-drive-note-${it.expense_note_id}`}>
                    <td className="px-3 py-2 text-zinc-800">{it.user_name}</td>
                    <td className="px-3 py-2 font-mono text-zinc-600">{it.month?.toString().padStart(2, "0")}/{it.year}</td>
                    <td className="px-3 py-2 font-mono text-zinc-700">{it.status}</td>
                    <td className="px-3 py-2 font-mono text-zinc-500">{it.current_folder || "—"}</td>
                    <td className="px-3 py-2 font-mono text-zinc-700">{it.expected_folder}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-${meta.color}-200 bg-${meta.color}-50 text-${meta.color}-700`}>
                        {meta.emoji} {meta.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {data.items.length > 100 && (
                <tr><td colSpan={6} className="px-3 py-2 text-center text-zinc-500 italic">… {data.items.length - 100} NDF supplémentaires tronquées</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* First-run / change-parent modal */}
      {showParentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="ndf-drive-parent-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <SettingsIcon size={18} /> Dossier Drive parent — Notes de frais
            </h2>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Collez l'URL Google Drive du dossier parent (ou son ID brut). Les NDF y seront archivées dans une arborescence <code>{`{Nom Collab.}/{Soumise|Validée|Réglée|Rejetée}/`}</code>.
            </p>
            {parentInfo?.parent_id && (
              <div className="bg-zinc-50 rounded p-2 text-[11px] font-mono text-zinc-600">
                Actuel : {parentInfo.parent_id}
              </div>
            )}
            <input
              type="text" value={parentInput}
              onChange={(e) => setParentInput(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              data-testid="ndf-drive-parent-input"
              className="w-full border border-zinc-200 rounded px-3 py-2 text-sm"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              {data?.parent_id && (
                <button onClick={() => setShowParentModal(false)} className="px-3 py-1.5 text-xs text-zinc-600 hover:text-zinc-900">
                  Annuler
                </button>
              )}
              <button
                onClick={saveParent} disabled={savingParent}
                data-testid="ndf-drive-parent-save"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium disabled:opacity-50"
              >
                {savingParent ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

